import { Inject, Injectable } from '@angular/core';
import { mapFetchResultData } from '@x/common/graph';
import { Observable } from 'rxjs';
import {
  ShopCheckoutCartGQL,
  ShopCheckoutOrderCanCheckoutGQL,
  ShopCheckoutOrderCancelGQL,
  ShopCheckoutOrderCouponEligibilityGQL,
  ShopCheckoutOrderDeleteItemGQL,
  ShopCheckoutOrderEnquireGQL,
  ShopCheckoutOrderGQL,
  ShopCheckoutOrderRequestShipmentSlotGQL,
  ShopCheckoutOrderShippingSlotsGQL,
  ShopCheckoutOrderSlotRequiredGQL,
  ShopCheckoutOrderStateGQL,
  ShopCheckoutOrderUpdateAddressGQL,
  ShopCheckoutOrderUpdateCollectionPointGQL,
  ShopCheckoutOrderUpdateCouponGQL,
  ShopCheckoutOrderUpdateGiftOptionsGQL,
  ShopCheckoutOrderUpdateInstructionsGQL,
  ShopCheckoutOrderUpdateItemGQL,
  ShopCheckoutUpdateItemsGQL,
} from './gql/shop-checkout-order.gql.generated';
import { AUTHORIZATION_HEADER_PROVIDER, AuthorizationHeaderProvider } from './types/shop-auth';
import {
  IShopCheckoutCartMutation,
  IShopCheckoutOrder,
  IShopCheckoutOrderCanCheckout,
  IShopCheckoutOrderCanCheckoutQuery,
  IShopCheckoutOrderCancelMutation,
  IShopCheckoutOrderCouponEligibility,
  IShopCheckoutOrderCouponEligibilityQuery,
  IShopCheckoutOrderDeleteItemMutation,
  IShopCheckoutOrderEnquireMutation,
  IShopCheckoutOrderRequestShipmentSlotMutation,
  IShopCheckoutOrderShipment,
  IShopCheckoutOrderShippingSlot,
  IShopCheckoutOrderSlotRequiredQuery,
  IShopCheckoutOrderState,
  IShopCheckoutOrderUpdateAddressMutation,
  IShopCheckoutOrderUpdateCollectionPointMutation,
  IShopCheckoutOrderUpdateCouponMutation,
  IShopCheckoutOrderUpdateGiftOptionsMutation,
  IShopCheckoutOrderUpdateInstructionsMutation,
  IShopCheckoutOrderUpdateItemMutation,
  IShopCheckoutUpdateItemsMutation,
  TShopCheckoutOrderQueryVariables,
  TShopCheckoutOrderShippingSlotsQuery,
} from './types/shop-checkout-order';

@Injectable()
export class ShopCheckoutOrderService {
  constructor(
    @Inject(AUTHORIZATION_HEADER_PROVIDER)
    private authHeader: AuthorizationHeaderProvider,
    private shopCheckoutOrderGQL: ShopCheckoutOrderGQL,
    private shopCheckoutOrderUpdateItemGQL: ShopCheckoutOrderUpdateItemGQL,
    private shopCheckoutOrderDeleteItemGQL: ShopCheckoutOrderDeleteItemGQL,
    private shopCheckoutOrderUpdateGiftOptionsGQL: ShopCheckoutOrderUpdateGiftOptionsGQL,
    private shopCheckoutOrderUpdateInstructionsGQL: ShopCheckoutOrderUpdateInstructionsGQL,
    private shopCheckoutOrderUpdateCouponGQL: ShopCheckoutOrderUpdateCouponGQL,
    private shopCheckoutOrderUpdateAddressGQL: ShopCheckoutOrderUpdateAddressGQL,
    private shopCheckoutOrderUpdateCollectionPointGQL: ShopCheckoutOrderUpdateCollectionPointGQL,
    private shopCheckoutOrderShippingSlotsGQL: ShopCheckoutOrderShippingSlotsGQL,
    private shopCheckoutOrderRequestShipmentSlotGQL: ShopCheckoutOrderRequestShipmentSlotGQL,
    private shopCheckoutOrderEnquireGQL: ShopCheckoutOrderEnquireGQL,
    private shopCheckoutOrderCancelGQL: ShopCheckoutOrderCancelGQL,
    private shopCheckoutOrderCouponEligibilityGQL: ShopCheckoutOrderCouponEligibilityGQL,
    private shopCheckoutOrderCanCheckoutGQL: ShopCheckoutOrderCanCheckoutGQL,
    private shopCheckoutOrderSlotRequiredGQL: ShopCheckoutOrderSlotRequiredGQL,
    private shopCheckoutOrderStateGQL: ShopCheckoutOrderStateGQL,
    private shopCheckoutCartGQL: ShopCheckoutCartGQL,
    private shopCheckoutUpdateItemsGQL: ShopCheckoutUpdateItemsGQL,
  ) {
    if (!authHeader) throw new Error('authHeader Provider is required');
  }

  fetchOrder(query: TShopCheckoutOrderQueryVariables): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutOrderGQL
      .fetch(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopOrder));
  }

  updateOrderItem(query: IShopCheckoutOrderUpdateItemMutation): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutOrderUpdateItemGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartItem));
  }

  deleteOrderItem(query: IShopCheckoutOrderDeleteItemMutation): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutOrderDeleteItemGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopDeleteCartItem));
  }

  updateGiftOptions(
    query: IShopCheckoutOrderUpdateGiftOptionsMutation,
  ): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutOrderUpdateGiftOptionsGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartGiftOptions));
  }

  updateInstructions(
    query: IShopCheckoutOrderUpdateInstructionsMutation,
  ): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutOrderUpdateInstructionsGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartInstructions));
  }

  updateCoupon(query: IShopCheckoutOrderUpdateCouponMutation): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutOrderUpdateCouponGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartCoupon));
  }

  updateAddress(query: IShopCheckoutOrderUpdateAddressMutation): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutOrderUpdateAddressGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartAddress));
  }

  updateCollectionPoint(
    query: IShopCheckoutOrderUpdateCollectionPointMutation,
  ): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutOrderUpdateCollectionPointGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartCollectionPoint));
  }

  fetchShippingSlots(
    query: TShopCheckoutOrderShippingSlotsQuery,
  ): Observable<IShopCheckoutOrderShippingSlot[]> {
    return this.shopCheckoutOrderShippingSlotsGQL
      .fetch(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopOrderShippingSlots));
  }

  requestShipmentSlot(
    query: IShopCheckoutOrderRequestShipmentSlotMutation,
  ): Observable<IShopCheckoutOrderShipment> {
    return this.shopCheckoutOrderRequestShipmentSlotGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopRequestOrderShipmentSlot));
  }

  enquire(input: IShopCheckoutOrderEnquireMutation): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutOrderEnquireGQL
      .mutate(input, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopEnquireOrder));
  }

  cancel(args: IShopCheckoutOrderCancelMutation): Observable<number> {
    return this.shopCheckoutOrderCancelGQL
      .mutate(args, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCancelOrder.id));
  }

  checkCouponEligibility(
    args: IShopCheckoutOrderCouponEligibilityQuery,
  ): Observable<IShopCheckoutOrderCouponEligibility> {
    return this.shopCheckoutOrderCouponEligibilityGQL
      .fetch(args, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCouponEligibility));
  }

  canCheckout(args: IShopCheckoutOrderCanCheckoutQuery): Observable<IShopCheckoutOrderCanCheckout> {
    return this.shopCheckoutOrderCanCheckoutGQL
      .fetch(args, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopOrder));
  }

  slotIsRequired(args: IShopCheckoutOrderSlotRequiredQuery): Observable<Boolean> {
    return this.shopCheckoutOrderSlotRequiredGQL
      .fetch(args, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopOrder.shipment?.slotRequired ?? false));
  }

  orderState(orderId: number): Observable<IShopCheckoutOrderState> {
    return this.shopCheckoutOrderStateGQL
      .fetch({ id: orderId }, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopOrder.state));
  }

  checkoutCart(input: IShopCheckoutCartMutation): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutCartGQL
      .mutate(input, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCheckoutOrder));
  }

  updateOrderItems(query: IShopCheckoutUpdateItemsMutation): Observable<IShopCheckoutOrder> {
    return this.shopCheckoutUpdateItemsGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartItems));
  }
}
